import { useHasMany, useIsHeaderCollapsed, useCampaignTypeById } from '../../store/campaignsStore';
import { useCampaignTypeContext } from '../../store/context/CampaignTypeContext';
import { useViewMode } from '../../../../store/app/appStore';
import type { CampaignTypes } from '../../types/campaign.types';

export type CampaignDataAttributes = {
  'data-has-many': boolean;
  'data-collapsed': boolean;
  'data-view': string;
};

interface UseCampaignDataAttributesParams<T extends Record<string, unknown>> {
  campaignId?: string;
  campaignType?: CampaignTypes;
  additionalProps?: T;
}

export const useCampaignDataAttributes = <T extends Record<string, unknown> = Record<string, never>>
  ({ campaignId, campaignType, additionalProps }: UseCampaignDataAttributesParams<T>) => {
    const fallbackType = useCampaignTypeById(campaignId);
    // Explicit prop and campaignId-derived type win; the section context is a
    // last resort so callers inside a card/section can omit the type entirely.
    const contextType = useCampaignTypeContext();
    const type = campaignType ?? fallbackType ?? contextType;
    const hasMany = useHasMany(type);
    const isCollapsed = useIsHeaderCollapsed();
    const viewMode = useViewMode();

    return {
      'data-has-many': hasMany,
      'data-collapsed': isCollapsed,
      'data-view': viewMode,
      ...additionalProps
    } as CampaignDataAttributes & T;
};
